import { useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsiveImage } from "src/components-mui/base/responsive-image/responsive-image";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { Container } from "src/components-mui/layout/container";
import { HeroSlideCtaBox, IHeroSlideCtaButton } from "src/components-mui/modules/hero-module/components/hero-slide-cta-box";
import { HeroSlideRegisterBox } from "src/components-mui/modules/hero-module/components/hero-slide-register-box";
import { HeroSliderSlideVideo } from "src/components-mui/modules/hero-module/components/hero-slider-slide-video";
import { HeroSliderWikifolios } from "src/components-mui/modules/hero-module/components/hero-slider-wikifolios";
import {
    HeroSliderButtonColor,
    HeroSliderSlideVariant,
    IHeroSliderWikifolioData,
} from "src/components-mui/modules/hero-module/hero-slider-module.types";
import useViewportSpy from "src/hooks/dom/use-viewport-spy";
import { useUserStore } from "src/stores/user-store";
import { IImage, IVideo } from "src/types/common";
import { HERO_SLIDER_SLIDE_CLASS } from "../constants";

export interface IHeroSliderSlideProps {
    title: string;
    description: string;
    desktopImage: IImage;
    desktopSmallImage: IImage;
    mobImage: IImage;
    tabImage: IImage;
    desktopVideo?: IVideo;
    desktopSmallVideo?: IVideo;
    mobVideo?: IVideo;
    tabVideo?: IVideo;
    registerButtonColor?: HeroSliderButtonColor;
    isTitleFontSizeBig?: boolean;
    variant?: HeroSliderSlideVariant;
    cta1?: IHeroSlideCtaButton | null;
    cta2?: IHeroSlideCtaButton | null;
    displayTitleAsH1?: boolean;
    delay?: number;
    wikifolios?: IHeroSliderWikifolioData[];
    isDarkModeEnabled?: boolean;
}

export const HeroSliderSlide = ({
    title,
    description,
    displayTitleAsH1,
    isTitleFontSizeBig,
    desktopImage,
    desktopSmallImage,
    variant,
    mobImage,
    tabImage,
    desktopVideo,
    mobVideo,
    desktopSmallVideo,
    tabVideo,
    registerButtonColor,
    cta1,
    cta2,
    wikifolios,
    isDarkModeEnabled,
}: IHeroSliderSlideProps) => {
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const { isLoggedIn } = useUserStore();
    const slideRef = useRef<HTMLDivElement>(null);

    const isInViewport = useViewportSpy(slideRef, { threshold: 0.1 });

    const hasVideo = desktopVideo || desktopSmallVideo || mobVideo || tabVideo;

    const handleVideoLoad = () => {
        setVideoLoaded(true);
        setVideoError(false);
    };

    const handleVideoError = () => {
        setVideoError(true);
        setVideoLoaded(false);
    };

    const handleVideoSourceChange = () => {
        setVideoLoaded(false);
        setVideoError(false);
    };

    return (
        <Box ref={slideRef} sx={{ flex: "0 0 100%", minWidth: 0 }} position="relative">
            <Box width="100%" height="100%" overflow="hidden">
                <ResponsiveImage
                    baseUrl={desktopImage.url}
                    alt={desktopImage?.alt}
                    width="100%"
                    height="100%"
                    srcSet={[
                        {
                            queryParams: { w: 768, h: 720 },
                            url: mobImage?.url ? mobImage.url : desktopImage.url,
                        },
                        {
                            queryParams: { w: 992, h: 720 },
                            url: tabImage?.url ? tabImage.url : desktopImage.url,
                        },
                        {
                            queryParams: { w: 1200, h: 650 },
                            url: desktopSmallImage?.url ? desktopSmallImage.url : desktopImage.url,
                        },
                        {
                            queryParams: { h: 760 },
                            url: desktopImage.url,
                        },
                    ]}
                    sx={{
                        minHeight: "100%",
                        objectFit: "cover",
                        objectPosition: "right center",
                        opacity: hasVideo && videoLoaded && !videoError ? 0 : 1,
                        transition: "opacity 0.3s ease-in-out",
                    }}
                    loading="eager"
                />

                {desktopVideo && (
                    <HeroSliderSlideVideo
                        desktopVideo={desktopVideo}
                        desktopSmallVideo={desktopSmallVideo}
                        mobVideo={mobVideo}
                        tabVideo={tabVideo}
                        onVideoLoad={handleVideoLoad}
                        onVideoError={handleVideoError}
                        onVideoSourceChange={handleVideoSourceChange}
                        isPaused={!isInViewport}
                        sx={{
                            opacity: videoLoaded && !videoError ? 1 : 0,
                            transition: "opacity 0.3s ease-in-out",
                        }}
                    />
                )}
            </Box>
            <Box
                className={HERO_SLIDER_SLIDE_CLASS}
                width="100%"
                height="100%"
                position="absolute"
                top="0"
                left="0"
                display={isInViewport ? "flex" : "none"}
                alignItems={["baseline", "center"]}
                sx={{ zIndex: 2 }}
            >
                <Container>
                    <Box maxWidth={["100%", "300px", "360px", "450px"]} pt={[4, 0]} color={isDarkModeEnabled ? "white" : "gray.800"}>
                        <WfHeading
                            fontSize={isTitleFontSizeBig ? { xs: "4xl", md: "5xl" } : { xs: "3xl", md: "4xl" }}
                            component={displayTitleAsH1 ? "h1" : "h2"}
                        >
                            <Typography component="span" fontSize="inherit" color="inherit" lineHeight="inherit" fontWeight="heavy">
                                {title}
                            </Typography>
                        </WfHeading>
                        <SimpleRichText mt={2} mb={3} text={description} />
                        {variant === "register" && !isLoggedIn && (
                            <HeroSlideRegisterBox registerButtonColor={registerButtonColor} isDarkModeEnabled={isDarkModeEnabled} />
                        )}
                        {variant === "cta" && <HeroSlideCtaBox cta1={cta1} cta2={cta2} />}
                    </Box>
                </Container>
                {wikifolios && wikifolios.length > 0 && <HeroSliderWikifolios wikifolios={wikifolios} />}
            </Box>
        </Box>
    );
};
